import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { ReportHeader } from '../../components/ReportHeader';
import { SortableTable } from '../../components/SortableTable';
import { Pagination } from '../../components/Pagination';
import { ExportButton } from '../../components/ExportButton';

const API = process.env.REACT_APP_BACKEND_URL;

// ── helpers ──────────────────────────────────────────────────────────────────
function fmt(n) { return (n ?? 0).toLocaleString(); }
function pct(v) { return `${Number(v ?? 0).toFixed(1)}%`; }

// ── sub-components ────────────────────────────────────────────────────────────
function KpiCard({ label, value, accent, testId }) {
  return (
    <div className="stat-card" data-testid={testId} style={{ borderTop: `3px solid ${accent}` }}>
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <h2 style={{
      fontSize: 15, fontWeight: 700, color: '#1e293b',
      margin: '24px 0 10px', letterSpacing: '-0.01em',
    }}>
      {children}
    </h2>
  );
}

// ── column definitions ────────────────────────────────────────────────────────
const LEARNER_COLS = [
  { key: 'name',            label: 'Name',          sortable: true },
  { key: 'email',           label: 'Email',         sortable: true },
  { key: 'domain',          label: 'Domain',        sortable: true },
  { key: 'enrolments',      label: 'Enrolments',    align: 'right', sortable: true },
  { key: 'completions',     label: 'Completions',   align: 'right', sortable: true },
  { key: 'completion_rate', label: 'Completion %',  align: 'right', sortable: true,
    render: v => `${Number(v ?? 0).toFixed(1)}%` },
  { key: 'joined_date',     label: 'Joined',        sortable: true },
];

const COURSE_COLS = [
  { key: 'course_name',     label: 'Course',        sortable: true },
  { key: 'solution_area',   label: 'Solution Area', sortable: true },
  { key: 'programme',       label: 'Programme',     sortable: true },
  { key: 'enrolments',      label: 'Enrolments',    align: 'right', sortable: true },
  { key: 'completions',     label: 'Completions',   align: 'right', sortable: true },
  { key: 'completion_rate', label: 'Completion %',  align: 'right', sortable: true,
    render: v => `${Number(v ?? 0).toFixed(1)}%` },
];

const VOUCHER_COLS = [
  { key: 'batch_id',    label: 'Batch ID',  sortable: true },
  { key: 'course_name', label: 'Course',    sortable: true },
  { key: 'total',       label: 'Total',     align: 'right', sortable: true },
  { key: 'issued',      label: 'Issued',    align: 'right', sortable: true },
  { key: 'used',        label: 'Used',      align: 'right', sortable: true },
  { key: 'expired',     label: 'Expired',   align: 'right' },
  { key: 'available',   label: 'Available', align: 'right', sortable: true },
  { key: 'expires_at',  label: 'Expires',   sortable: true },
];

const TABS = ['learners', 'courses', 'vouchers'];

// ── main page ─────────────────────────────────────────────────────────────────
export default function PartnerSelfServiceDashboard() {
  // Current user info — fetched from /api/partner/me on mount
  const [myInfo, setMyInfo] = useState(null);

  // Period (driven by ReportHeader)
  const [fyLabel, setFyLabel] = useState('');

  // Admin override — searchable partner typeahead
  const [adminSearch, setAdminSearch]       = useState('');
  const [adminResults, setAdminResults]     = useState([]);
  const [adminSearching, setAdminSearching] = useState(false);
  const [showDropdown, setShowDropdown]     = useState(false);
  const [adminPoidLabel, setAdminPoidLabel] = useState('');
  const [adminPoid, setAdminPoid]           = useState('');
  const adminSearchRef                       = React.useRef(null);

  // KPIs + Trend
  const [kpis, setKpis]           = useState(null);
  const [trendData, setTrendData] = useState([]);
  const [kpisLoading, setKpisLoading] = useState(false);

  // Tab
  const [activeTab, setActiveTab] = useState('learners');

  // Learners
  const [learners, setLearners]           = useState({ data: [], total: 0 });
  const [learnersLoading, setLearnersLoading] = useState(false);
  const [learnerPage, setLearnerPage]     = useState(1);
  const [learnerPageSize, setLearnerPageSize] = useState(50);
  const [learnerSearch, setLearnerSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [learnerSortKey, setLearnerSortKey] = useState('completions');
  const [learnerSortDir, setLearnerSortDir] = useState('desc');

  // Courses
  const [courses, setCourses]           = useState({ data: [], total: 0 });
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [coursePage, setCoursePage]     = useState(1);
  const [coursePageSize, setCoursePageSize] = useState(50);
  const [courseFilters, setCourseFilters] = useState({ search: '', solution_area: '' });
  const [appliedCourseFilters, setAppliedCourseFilters] = useState({ search: '', solution_area: '' });
  const [courseSortKey, setCourseSortKey] = useState('enrolments');
  const [courseSortDir, setCourseSortDir] = useState('desc');
  const [solutionAreas, setSolutionAreas] = useState([]);

  // Vouchers
  const [vouchers, setVouchers]           = useState({ data: [], total: 0 });
  const [vouchersLoading, setVouchersLoading] = useState(false);
  const [voucherPage, setVoucherPage]     = useState(1);
  const [voucherPageSize, setVoucherPageSize] = useState(50);
  const [voucherSortKey, setVoucherSortKey] = useState('expires_at');
  const [voucherSortDir, setVoucherSortDir] = useState('asc');

  const [error, setError] = useState('');

  // Derived role flags (set once /api/partner/me resolves)
  const isAdmin   = myInfo?.role === 'tf_admin';
  // TF admin must select a partner; partner manager is scoped automatically via JWT
  const dataReady = !!myInfo && (!isAdmin || !!adminPoid);

  // Admin poid param — appended to all API calls when admin has overridden partner
  const poidParam = isAdmin && adminPoid ? { partner_one_id: adminPoid } : {};

  // ── Load current user role + partner scope ────────────────────────────────
  useEffect(() => {
    axios.get(`${API}/api/partner/me`, { withCredentials: true })
      .then(({ data }) => setMyInfo(data))
      // Safe fallback: bearer-token callers are always TF admins
      .catch(() => setMyInfo({ role: 'tf_admin', is_admin: true, partner_one_id: null, partner_name: null }));
  }, []);

  // ── Load solution areas once ──────────────────────────────────────────────
  useEffect(() => {
    axios.get(`${API}/api/solution-areas`, { withCredentials: true })
      .then(r => setSolutionAreas(r.data.map(sa => sa.name)))
      .catch(() => {});
  }, []);

  // ── Admin partner typeahead — debounced search ────────────────────────────
  useEffect(() => {
    if (!isAdmin || adminSearch.trim().length < 2) {
      setAdminResults([]);
      setShowDropdown(false);
      return;
    }
    const timer = setTimeout(() => {
      setAdminSearching(true);
      axios.get(`${API}/api/partner/search-partners`, {
        params: { q: adminSearch.trim() },
        withCredentials: true,
      })
        .then(({ data }) => { setAdminResults(data); setShowDropdown(data.length > 0); })
        .catch(() => { setAdminResults([]); setShowDropdown(false); })
        .finally(() => setAdminSearching(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [adminSearch, isAdmin]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (adminSearchRef.current && !adminSearchRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ── KPIs + Trend on FY/adminPoid change ──────────────────────────────────
  useEffect(() => {
    if (!fyLabel || !dataReady) return;
    setKpisLoading(true);
    setError('');
    Promise.all([
      axios.get(`${API}/api/partner/kpis`,  { params: { fy_label: fyLabel, ...poidParam }, withCredentials: true }),
      axios.get(`${API}/api/partner/trend`, { params: { fy_label: fyLabel, ...poidParam }, withCredentials: true }),
    ])
      .then(([kpiRes, trendRes]) => {
        setKpis(kpiRes.data);
        setTrendData(trendRes.data);
      })
      .catch(e => {
        setError(
          e.response?.status === 403
            ? 'No partner data is associated with your account. Contact your administrator.'
            : 'Failed to load partner data. Please try again.'
        );
      })
      .finally(() => setKpisLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fyLabel, adminPoid, dataReady]);

  // ── Learners ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (activeTab !== 'learners' || !fyLabel || !dataReady) return;
    setLearnersLoading(true);
    axios.get(`${API}/api/partner/learners`, {
      params: {
        fy_label: fyLabel,
        page: learnerPage, page_size: learnerPageSize,
        sort_by: learnerSortKey, sort_dir: learnerSortDir,
        ...(appliedSearch ? { search: appliedSearch } : {}),
        ...poidParam,
      },
      withCredentials: true,
    })
      .then(({ data }) => setLearners({ data: data.data, total: data.total }))
      .catch(console.error)
      .finally(() => setLearnersLoading(false));
  }, [activeTab, fyLabel, learnerPage, learnerPageSize, appliedSearch, learnerSortKey, learnerSortDir, adminPoid]); // eslint-disable-line

  // ── Courses ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (activeTab !== 'courses' || !fyLabel || !dataReady) return;
    setCoursesLoading(true);
    const { search, solution_area } = appliedCourseFilters;
    axios.get(`${API}/api/partner/courses`, {
      params: {
        fy_label: fyLabel,
        page: coursePage, page_size: coursePageSize,
        sort_by: courseSortKey, sort_dir: courseSortDir,
        ...(search ? { search } : {}),
        ...(solution_area ? { solution_area } : {}),
        ...poidParam,
      },
      withCredentials: true,
    })
      .then(({ data }) => setCourses({ data: data.data, total: data.total }))
      .catch(console.error)
      .finally(() => setCoursesLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, fyLabel, coursePage, coursePageSize, courseSortKey, courseSortDir,
      appliedCourseFilters.search, appliedCourseFilters.solution_area, adminPoid]);

  // ── Vouchers ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (activeTab !== 'vouchers' || !dataReady) return;
    setVouchersLoading(true);
    axios.get(`${API}/api/partner/vouchers`, {
      params: {
        page: voucherPage, page_size: voucherPageSize,
        sort_by: voucherSortKey, sort_dir: voucherSortDir,
        ...poidParam,
      },
      withCredentials: true,
    })
      .then(({ data }) => setVouchers({ data: data.data, total: data.total }))
      .catch(console.error)
      .finally(() => setVouchersLoading(false));
  }, [activeTab, voucherPage, voucherPageSize, voucherSortKey, voucherSortDir, adminPoid]); // eslint-disable-line

  // ── ReportHeader onChange ─────────────────────────────────────────────────
  const handlePeriodChange = useCallback(({ fyLabel: fy }) => {
    if (!fy) return;
    setFyLabel(fy);
    setLearnerPage(1);
    setCoursePage(1);
    setVoucherPage(1);
  }, []);

  // ── Sort handlers (reset page on sort change) ─────────────────────────────
  function handleLearnerSort(sorts) {
    const s = sorts[0] || { key: 'completions', direction: 'desc' };
    setLearnerSortKey(s.key);
    setLearnerSortDir(s.direction);
    setLearnerPage(1);
  }
  function handleCourseSort(sorts) {
    const s = sorts[0] || { key: 'enrolments', direction: 'desc' };
    setCourseSortKey(s.key);
    setCourseSortDir(s.direction);
    setCoursePage(1);
  }
  function handleVoucherSort(sorts) {
    const s = sorts[0] || { key: 'expires_at', direction: 'asc' };
    setVoucherSortKey(s.key);
    setVoucherSortDir(s.direction);
    setVoucherPage(1);
  }

  // ── Export all learners ───────────────────────────────────────────────────
  const fetchAllLearners = useCallback(async () => {
    const { data } = await axios.get(`${API}/api/partner/export/learners`, {
      params: {
        fy_label: fyLabel,
        sort_by: learnerSortKey, sort_dir: learnerSortDir,
        ...(appliedSearch ? { search: appliedSearch } : {}),
        ...poidParam,
      },
      withCredentials: true,
    });
    return data;
  }, [fyLabel, learnerSortKey, learnerSortDir, appliedSearch]);

  // ── Tab switch ────────────────────────────────────────────────────────────
  function switchTab(tab) {
    setActiveTab(tab);
  }

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <>
      <ReportHeader
        reportTitle={
          myInfo?.role === 'partner_reporting_manager' && myInfo?.partner_name
            ? `Partner Dashboard — ${myInfo.partner_name}`
            : 'Partner Dashboard'
        }
        primaryTable="completion_reports"
        onChange={handlePeriodChange}
      />

      <div data-testid="partner-self-service-dashboard" style={{ padding: '0 16px 32px' }}>

        {/* Admin override panel — only visible to TF admins */}
        {isAdmin && (
          <div
            data-testid="admin-override-panel"
            style={{
              margin: '14px 0 4px',
              padding: '10px 14px',
              background: '#fffbeb',
              border: '1px solid #fde68a',
              borderRadius: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              flexWrap: 'wrap',
            }}
          >
            <span style={{ fontSize: 12, fontWeight: 600, color: '#92400e', flexShrink: 0 }}>
              Admin — view partner:
            </span>

            {/* Typeahead search */}
            <div ref={adminSearchRef} style={{ position: 'relative', flex: '1 1 280px', maxWidth: 360 }}>
              <input
                data-testid="admin-partner-search"
                type="text"
                placeholder={adminPoid ? adminPoidLabel : 'Search partner name or ID…'}
                value={adminSearch}
                onChange={e => { setAdminSearch(e.target.value); if (!e.target.value) { setAdminPoid(''); setAdminPoidLabel(''); } }}
                onFocus={() => { if (adminResults.length > 0) setShowDropdown(true); }}
                style={{
                  width: '100%', height: 32, fontSize: 12, padding: '0 32px 0 10px',
                  border: '1px solid #fbbf24', borderRadius: 6, outline: 'none',
                  background: adminPoid ? '#fef3c7' : '#fff',
                }}
              />
              {/* Clear button */}
              {(adminSearch || adminPoid) && (
                <button
                  data-testid="admin-partner-clear"
                  onClick={() => {
                    setAdminSearch(''); setAdminPoid(''); setAdminPoidLabel('');
                    setAdminResults([]); setShowDropdown(false);
                    setKpis(null); setTrendData([]);
                    setLearnerPage(1); setCoursePage(1); setVoucherPage(1);
                  }}
                  style={{
                    position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: '#92400e', fontSize: 14, lineHeight: 1, padding: 0,
                  }}
                  title="Clear partner selection"
                >
                  ×
                </button>
              )}
              {/* Searching indicator */}
              {adminSearching && (
                <span style={{
                  position: 'absolute', right: 28, top: '50%', transform: 'translateY(-50%)',
                  fontSize: 10, color: '#d97706',
                }}>…</span>
              )}
              {/* Dropdown results */}
              {showDropdown && adminResults.length > 0 && (
                <ul
                  data-testid="admin-partner-dropdown"
                  style={{
                    position: 'absolute', top: '110%', left: 0, right: 0,
                    background: '#fff', border: '1px solid #fbbf24', borderRadius: 6,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    listStyle: 'none', margin: 0, padding: '4px 0',
                    zIndex: 1000, maxHeight: 220, overflowY: 'auto',
                  }}
                >
                  {adminResults.map(p => (
                    <li
                      key={p.poid}
                      data-testid={`admin-partner-option-${p.poid}`}
                      onMouseDown={e => {
                        e.preventDefault();
                        setAdminPoid(p.poid);
                        setAdminPoidLabel(p.name);
                        setAdminSearch('');
                        setShowDropdown(false);
                        setKpis(null);
                        setLearnerPage(1); setCoursePage(1); setVoucherPage(1);
                      }}
                      style={{
                        padding: '7px 12px', fontSize: 12, cursor: 'pointer',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        gap: 8,
                      }}
                      onMouseEnter={e => (e.currentTarget.style.background = '#fef3c7')}
                      onMouseLeave={e => (e.currentTarget.style.background = '')}
                    >
                      <span style={{ fontWeight: 500, color: '#1e293b', flexShrink: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
                      <span style={{ fontSize: 10, color: '#92400e', fontFamily: 'monospace', flexShrink: 0 }}>{p.poid}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Selected partner badge */}
            {adminPoid && !adminSearch && (
              <span
                data-testid="admin-poid-selected"
                style={{
                  fontSize: 11, color: '#78350f', background: '#fde68a',
                  borderRadius: 4, padding: '2px 8px', fontWeight: 600,
                }}
              >
                Viewing: {adminPoidLabel || adminPoid} ({adminPoid})
              </span>
            )}
          </div>
        )}

        {/* Error banner */}
        {error && (
          <div data-testid="partner-error" style={{
            background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca',
            borderRadius: 6, padding: '10px 14px', margin: '14px 0', fontSize: 13,
          }}>
            {error}
          </div>
        )}

        {/* TF Admin — no partner selected yet */}
        {myInfo && isAdmin && !adminPoid && !error && (
          <div
            data-testid="select-partner-prompt"
            style={{ color: '#64748b', fontSize: 14, padding: '40px 0', textAlign: 'center' }}
          >
            Use the panel above to select a partner and view their dashboard.
          </div>
        )}

        {/* Loading skeleton for initial KPI load */}
        {kpisLoading && !kpis && (
          <div style={{ color: '#64748b', fontSize: 14, padding: '40px 0', textAlign: 'center' }}>
            Loading partner data…
          </div>
        )}

        {/* KPI cards */}
        {kpis && (
          <>
            <SectionTitle>Performance — {fyLabel}</SectionTitle>
            <div
              className="stats-grid"
              data-testid="partner-kpi-cards"
              style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))' }}
            >
              <KpiCard label="New Registrations" value={fmt(kpis.registrations)}     accent="#f59e0b" testId="kpi-registrations"    />
              <KpiCard label="Enrolments"         value={fmt(kpis.enrolments)}        accent="#3b82f6" testId="kpi-enrolments"       />
              <KpiCard label="Completions"        value={fmt(kpis.completions)}       accent="#22c55e" testId="kpi-completions"      />
              <KpiCard label="Completion Rate"    value={pct(kpis.completion_rate)}   accent="#6366f1" testId="kpi-completion-rate"  />
              <KpiCard
                label="Seats Used"
                value={kpis.seats_allocated > 0
                  ? `${fmt(kpis.seats_used)} / ${fmt(kpis.seats_allocated)}`
                  : fmt(kpis.seats_used)}
                accent="#06b6d4"
                testId="kpi-seats-used"
              />
            </div>
          </>
        )}

        {/* Trend chart */}
        {trendData.length > 0 && (
          <>
            <SectionTitle>Monthly Trend</SectionTitle>
            <div className="report-card" data-testid="partner-trend-chart" style={{ padding: '20px 16px 8px' }}>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={trendData} margin={{ top: 4, right: 24, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={false} width={52} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6, border: '1px solid #e2e8f0' }} />
                  <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} iconType="circle" />
                  <Line type="monotone" dataKey="enrolments"  name="Enrolments"  stroke="#3b82f6" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                  <Line type="monotone" dataKey="completions" name="Completions" stroke="#22c55e" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </>
        )}

        {/* Tabs — only show once KPIs are loaded */}
        {kpis && (
          <>
            {/* Tab bar */}
            <div
              data-testid="partner-tabs"
              style={{ borderBottom: '2px solid #e2e8f0', display: 'flex', gap: 0, marginTop: 28 }}
            >
              {TABS.map(tab => (
                <button
                  key={tab}
                  data-testid={`tab-${tab}`}
                  onClick={() => switchTab(tab)}
                  style={{
                    padding: '8px 22px', fontSize: 13, fontWeight: 600,
                    border: 'none', background: 'none', cursor: 'pointer',
                    borderBottom: activeTab === tab ? '2px solid #6366f1' : '2px solid transparent',
                    color: activeTab === tab ? '#6366f1' : '#64748b',
                    marginBottom: -2,
                    transition: 'color 0.15s',
                  }}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>

            {/* ── Learners tab ── */}
            {activeTab === 'learners' && (
              <div data-testid="tab-content-learners" style={{ marginTop: 16 }}>
                <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                  <input
                    data-testid="learner-search-input"
                    className="filter-input"
                    placeholder="Search name or email…"
                    value={learnerSearch}
                    onChange={e => setLearnerSearch(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') { setAppliedSearch(learnerSearch); setLearnerPage(1); }
                    }}
                    style={{ flex: '1 1 220px', maxWidth: 320 }}
                  />
                  <button
                    data-testid="learner-search-apply"
                    className="btn-primary"
                    onClick={() => { setAppliedSearch(learnerSearch); setLearnerPage(1); }}
                    style={{ fontSize: 13, padding: '7px 14px' }}
                  >
                    Apply
                  </button>
                  <button
                    data-testid="learner-search-reset"
                    className="btn-secondary"
                    onClick={() => { setLearnerSearch(''); setAppliedSearch(''); setLearnerPage(1); }}
                    style={{ fontSize: 13, padding: '7px 14px' }}
                  >
                    Reset
                  </button>
                  <div style={{ marginLeft: 'auto' }}>
                    <ExportButton
                      fetchAllFn={fetchAllLearners}
                      filename={`partner-learners_${fyLabel}`}
                      columns={LEARNER_COLS}
                      label="Export Learners"
                    />
                  </div>
                </div>
                <div className="report-card">
                  <SortableTable
                    columns={LEARNER_COLS}
                    data={learners.data}
                    loading={learnersLoading}
                    defaultSort={{ key: learnerSortKey, direction: learnerSortDir }}
                    onSortChange={handleLearnerSort}
                  />
                  <Pagination
                    totalCount={learners.total}
                    pageSize={learnerPageSize}
                    currentPage={learnerPage}
                    onPageChange={setLearnerPage}
                    onPageSizeChange={ps => { setLearnerPageSize(ps); setLearnerPage(1); }}
                  />
                </div>
              </div>
            )}

            {/* ── Courses tab ── */}
            {activeTab === 'courses' && (
              <div data-testid="tab-content-courses" style={{ marginTop: 16 }}>
                <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                  <input
                    data-testid="course-search-input"
                    className="filter-input"
                    placeholder="Search course name…"
                    value={courseFilters.search}
                    onChange={e => setCourseFilters(f => ({ ...f, search: e.target.value }))}
                    onKeyDown={e => {
                      if (e.key === 'Enter') { setAppliedCourseFilters({ ...courseFilters }); setCoursePage(1); }
                    }}
                    style={{ flex: '1 1 200px', maxWidth: 280 }}
                  />
                  <select
                    data-testid="course-solution-area-filter"
                    value={courseFilters.solution_area}
                    onChange={e => setCourseFilters(f => ({ ...f, solution_area: e.target.value }))}
                    style={{
                      height: 34, fontSize: 12, border: '1px solid #e2e8f0',
                      borderRadius: 6, padding: '0 8px', background: '#fff', color: '#334155',
                      minWidth: 180,
                    }}
                  >
                    <option value="">All Solution Areas</option>
                    {solutionAreas.map(sa => <option key={sa} value={sa}>{sa}</option>)}
                  </select>
                  <button
                    data-testid="course-filter-apply"
                    className="btn-primary"
                    onClick={() => { setAppliedCourseFilters({ ...courseFilters }); setCoursePage(1); }}
                    style={{ fontSize: 13, padding: '7px 14px' }}
                  >
                    Apply
                  </button>
                  <button
                    data-testid="course-filter-reset"
                    className="btn-secondary"
                    onClick={() => {
                      const empty = { search: '', solution_area: '' };
                      setCourseFilters(empty);
                      setAppliedCourseFilters(empty);
                      setCoursePage(1);
                    }}
                    style={{ fontSize: 13, padding: '7px 14px' }}
                  >
                    Reset
                  </button>
                </div>
                <div className="report-card">
                  <SortableTable
                    columns={COURSE_COLS}
                    data={courses.data}
                    loading={coursesLoading}
                    defaultSort={{ key: courseSortKey, direction: courseSortDir }}
                    onSortChange={handleCourseSort}
                  />
                  <Pagination
                    totalCount={courses.total}
                    pageSize={coursePageSize}
                    currentPage={coursePage}
                    onPageChange={setCoursePage}
                    onPageSizeChange={ps => { setCoursePageSize(ps); setCoursePage(1); }}
                  />
                </div>
              </div>
            )}

            {/* ── Vouchers tab ── */}
            {activeTab === 'vouchers' && (
              <div data-testid="tab-content-vouchers" style={{ marginTop: 16 }}>
                <div className="report-card">
                  <SortableTable
                    columns={VOUCHER_COLS}
                    data={vouchers.data}
                    loading={vouchersLoading}
                    defaultSort={{ key: voucherSortKey, direction: voucherSortDir }}
                    onSortChange={handleVoucherSort}
                  />
                  <Pagination
                    totalCount={vouchers.total}
                    pageSize={voucherPageSize}
                    currentPage={voucherPage}
                    onPageChange={setVoucherPage}
                    onPageSizeChange={ps => { setVoucherPageSize(ps); setVoucherPage(1); }}
                  />
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
